import * as React from "react";
import { Stack, Typography } from "@mui/material";
import { colors } from "@/styles/constants";
import { Person, PersonErrors } from "@/types";
import { BooleanInput } from "@/components/atoms/BooleanInput";
import { DateInput } from "@/components/atoms/DateInput";
import { EmailAddressInput } from "@/components/atoms";
import { Header } from "@/components/atoms/Header";
import { PhoneNumberTextInput } from "@/components/atoms";
import { TextInput } from "@/components/atoms/TextInput";
import { NewTitleInput } from "@/components/atoms/TitleInput";
import { NewAddressInput } from "@/components/molecules/AddressInput";
import { FormField } from "@/components/molecules/FormField";
import { FormStack } from "@/components/molecules/FormStack";
import { InfoBoxTrigger } from "@/components/molecules";
import { RelationToDeceased } from "@/components/molecules";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { VulnerableInfoBox } from "@/components/molecules/VulnerableInfoBox";
import { nokRequiredFields } from "@/types";
import { useBrandingContext } from "@/contexts/BrandingContext";

const currentSection = "nok";

export type NokDetailsViewProps = {
  serviceProvider: { customForm?: string };
  record: Person;
  errors: PersonErrors;
  onDateOfBirthChange: any;
  onNext: () => void;
  onFieldChange: any;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  onIsVulnerableChange: (value: boolean) => void;
  showIDVerificationDialog: boolean;
  hideIDVerificationDialog: () => void;
};

export const NokDetailsView: React.FC<NokDetailsViewProps> = ({
  serviceProvider,
  errors,
  record,
  onDateOfBirthChange,
  onNext,
  onFieldChange,
  onSectionClick,
  onIsVulnerableChange,
  updateTemplate,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy: false,
      currentSection,
      onNext,
      saveLabel: null,
      onSectionClick,
    });
  }, [onNext, onSectionClick, updateTemplate]);
  const { brand } = useBrandingContext();

  return (
    <Stack rowGap={4}>
      <Header level={1} color={colors.accentTeal}>
        {" "}
        Next of Kin information{" "}
      </Header>
      <Typography variant="body1">
        {" "}
        <b>Please provide details for the closest Next of Kin.</b> When you submit this form, they
        will be sent an email asking them to sign a grant of authority. The grant of authority gives
        you (and {brand}) permission to act on behalf of the Estate. We cannot proceed without it.
      </Typography>

      <FormStack>
        <FormField halfWidthByItself label="Title" required={nokRequiredFields.get("title")}>
          <NewTitleInput
            name="title"
            onValueChange={(value) => onFieldChange({ target: { name: "title", value } })}
            value={record.title || ""}
            error={errors?.title}
          />
        </FormField>

        <FormField halfWidth label="First name" required={nokRequiredFields.get("firstName")}>
          <TextInput
            name="firstName"
            value={record.firstName || ""}
            error={errors?.firstName}
            onChange={onFieldChange}
          />
        </FormField>

        <FormField halfWidth label="Last name" required={nokRequiredFields.get("lastName")}>
          <TextInput
            name="lastName"
            value={record.lastName || ""}
            error={errors?.lastName}
            onChange={onFieldChange}
          />
        </FormField>

        {record.role !== "solicitor" && (
          <FormField
            halfWidthByItself
            label="Date of birth"
            required={nokRequiredFields.get("dateOfBirth")}
          >
            <DateInput
              value={record.dateOfBirth || ""}
              onValueChange={onDateOfBirthChange}
              error={errors?.dateOfBirth}
              pastOnly
            />
          </FormField>
        )}

        <FormField label="Address" required={nokRequiredFields.get("address")}>
          <NewAddressInput
            name="address"
            value={record.address || ""}
            onChange={onFieldChange}
            error={errors?.address}
          />
        </FormField>

        <FormField halfWidth label="City or Town" required={nokRequiredFields.get("city")}>
          <TextInput
            name="city"
            value={record.city || ""}
            onChange={onFieldChange}
            error={errors?.city}
          />
        </FormField>

        <FormField halfWidth label="Postcode" required={nokRequiredFields.get("postcode")}>
          <TextInput
            name="postcode"
            value={record.postcode || ""}
            onChange={onFieldChange}
            error={errors?.postcode}
          />
        </FormField>

        <FormField
          halfWidth
          label="Telephone number"
          required={nokRequiredFields.get("contactNumber")}
        >
          <PhoneNumberTextInput
            name="contactNumber"
            value={record.contactNumber || ""}
            onChange={onFieldChange}
            error={errors?.contactNumber}
          />
        </FormField>

        <FormField halfWidth label="Email address" required={nokRequiredFields.get("email")}>
          <EmailAddressInput
            name="email"
            value={record.email || ""}
            onChange={onFieldChange}
            error={errors?.email} // autoFocus={updatingNotifierEmailAddress}
          />
        </FormField>

        {record.role !== "solicitor" && (
          <FormStack substack spacing={0}>
            <FormField>
              <BooleanInput
                label={
                  <span>
                    I consider this person to be vulnerable (temporarily or long-term) and think
                    they may appreciate additional support.{" "}
                    <InfoBoxTrigger white width="448px" content={VulnerableInfoBox()} />
                  </span>
                }
                value={record.isVulnerable}
                onValueChange={onIsVulnerableChange}
              />
            </FormField>

            {serviceProvider.customForm === "portsmouth-water" && record.isVulnerable && (
              <FormField>
                {" "}
                To notify Portsmouth Water of vulnerability,{" "}
                <a
                  href="https://www.portsmouthwater.co.uk/customer-services/priority-services/"
                  rel="noreferrer"
                  target="_blank"
                >
                  click here
                </a>{" "}
                to sign up to their priority services register.
              </FormField>
            )}
          </FormStack>
        )}

        {record.role !== "solicitor" && (
          <FormField
            halfWidthByItself
            label={<span>The person who died was their... &nbsp;</span>}
            required={nokRequiredFields.get("relationshipToDeceased")}
          >
            <br></br>
            <RelationToDeceased
              name="relationshipToDeceased"
              value={record.relationshipToDeceased || ""}
              otherValue={record.relationshipToDeceasedOther || ""}
              error={errors.relationshipToDeceased || errors.relationshipToDeceasedOther}
              onFieldChange={onFieldChange}
            />
          </FormField>
        )}
      </FormStack>
    </Stack>
  );
};
